/**
 * Asking the server a question, and knowing when there was NO ANSWER.
 *
 * "The server said no" and "nobody answered" used to look the same to a
 * screen: both came back as a failed lookup, in red, and the scan was thrown
 * away. They are different things. A no is a verdict on the pallet. No answer
 * is a forklift in a container with a weak signal, and says nothing about the
 * pallet at all — the scan should be kept and asked about again (owner
 * decision 2026-10-08).
 *
 * `reach` is `fetch` with that distinction made, and with a time limit: on a
 * signal too weak to carry a request, a plain fetch neither fails nor returns,
 * and the card would spin until someone gave up on it.
 */

export class LookupUnreachableError extends Error {
  constructor(
    message: string,
    /** True when something answered, but could not give an answer (a 5xx): the connection itself is fine. */
    readonly reachedServer: boolean,
  ) {
    super(message)
  }
}

/** How long a lookup is given before a weak signal counts as no signal. */
export const REACH_TIMEOUT_MS = 10_000

/** Statuses that mean "ask again later", as the sync queue reads them too (classify.ts). */
export function isNoAnswerStatus(status: number): boolean {
  return status >= 500 || status === 408 || status === 429
}

export async function reach(
  url: string,
  init: RequestInit = {},
  options: { fetcher?: typeof fetch, timeoutMs?: number } = {},
): Promise<Response> {
  const fetcher = options.fetcher ?? fetch
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), options.timeoutMs ?? REACH_TIMEOUT_MS)
  let response: Response
  try {
    response = await fetcher(url, { ...init, signal: controller.signal })
  } catch {
    throw new LookupUnreachableError('No answer from the server', false)
  } finally {
    clearTimeout(timer)
  }
  if (isNoAnswerStatus(response.status)) {
    throw new LookupUnreachableError(`The server could not answer (${response.status})`, true)
  }
  return response
}
